import React, { useEffect, useState, useCallback } from "react";
import { DEFAULT_RULES } from "./RulesSheet";
import { Trophy, Save, Search, UserPlus, UserMinus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "../lib/api";
import { logError } from "../lib/logger";
import { monthLabel } from "../pages/Championship";

const inputCls = "w-full px-3 py-2 rounded-md bg-[#0A0A0A] border border-white/10 text-white text-sm focus:border-amber-500 focus:outline-none transition-colors";
const fail = (err) => toast.error(formatError(err.response?.data?.detail));

const EntryRow = ({ u, entry, month, onSaved }) => {
  const [pct, setPct] = useState(entry?.return_pct ?? "");
  const [usd, setUsd] = useState(entry?.profit_usd ?? "");
  useEffect(() => { setPct(entry?.return_pct ?? ""); setUsd(entry?.profit_usd ?? ""); }, [entry]);
  const save = async () => {
    if (pct === "" || isNaN(Number(pct))) return toast.error("Inserisci il rendimento %");
    try {
      await api.put("/admin/championship/entry", { month, user_id: u.user_id, return_pct: Number(pct),
        profit_usd: usd === "" ? null : Number(usd) });
      toast.success(`Salvato: ${u.name}`); onSaved();
    } catch (err) { fail(err); }
  };
  const remove = async () => {
    try { await api.delete("/admin/championship/entry", { params: { month, user_id: u.user_id } }); onSaved(); }
    catch (err) { fail(err); }
  };
  return (
    <div data-testid={`cup-admin-row-${u.user_id}`} className="grid grid-cols-12 gap-2 items-center rounded-md border border-white/10 bg-black/30 px-3 py-2">
      <div className="col-span-12 sm:col-span-4 text-sm text-white truncate">{u.name}<div className="text-[11px] text-zinc-600 truncate">{u.email}</div></div>
      <input data-testid={`cup-pct-${u.user_id}`} type="number" step="0.01" placeholder="Rend. %" value={pct}
        onChange={(e) => setPct(e.target.value)} className={`${inputCls} col-span-4 sm:col-span-3`} />
      <input data-testid={`cup-usd-${u.user_id}`} type="number" step="0.01" placeholder="Profitto $" value={usd}
        onChange={(e) => setUsd(e.target.value)} className={`${inputCls} col-span-4 sm:col-span-3`} />
      <div className="col-span-4 sm:col-span-2 flex justify-end gap-1">
        <button data-testid={`cup-save-${u.user_id}`} onClick={save} title="Salva"
          className="p-2 rounded-md bg-amber-600 text-white hover:bg-amber-500 transition-colors"><Save size={14} /></button>
        {entry && (
          <button data-testid={`cup-del-${u.user_id}`} onClick={remove} title="Rimuovi dal mese"
            className="p-2 rounded-md text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"><Trash2 size={14} /></button>
        )}
      </div>
    </div>
  );
};

const UserSearch = ({ onChanged }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const search = async () => {
    try { const { data } = await api.get("/admin/championship/users", { params: { q } }); setResults(data.results); }
    catch (err) { fail(err); }
  };
  const toggle = async (u) => {
    try {
      await api.post(`/admin/championship/users/${u.user_id}`, { enabled: !u.enabled });
      toast.success(u.enabled ? "Utente disabilitato" : "Utente abilitato alla Trading Cup");
      setResults((r) => r.map((x) => (x.user_id === u.user_id ? { ...x, enabled: !x.enabled } : x)));
      onChanged();
    } catch (err) { fail(err); }
  };
  return (
    <div>
      <div className="flex gap-2">
        <input data-testid="cup-user-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca utente per email o nome"
          onKeyDown={(e) => e.key === "Enter" && search()} className={inputCls} />
        <button data-testid="cup-user-search-btn" onClick={search}
          className="px-4 rounded-md bg-white/5 border border-white/10 text-zinc-300 hover:text-white transition-colors"><Search size={15} /></button>
      </div>
      {results.length > 0 && (
        <div className="mt-2 space-y-1">
          {results.map((u) => (
            <div key={u.user_id} className="flex items-center justify-between gap-2 text-sm rounded-md bg-black/30 border border-white/10 px-3 py-2">
              <span className="text-white truncate">{u.name} <span className="text-zinc-600 text-xs">{u.email}</span></span>
              <button data-testid={`cup-toggle-${u.user_id}`} onClick={() => toggle(u)}
                className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border transition-colors ${u.enabled
                  ? "border-red-500/30 text-red-400 hover:bg-red-500/10" : "border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"}`}>
                {u.enabled ? <><UserMinus size={12} /> Disabilita</> : <><UserPlus size={12} /> Abilita</>}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export function ChampionshipAdmin() {
  const [month, setMonth] = useState("");
  const [cup, setCup] = useState(null);
  const [enabled, setEnabled] = useState([]);
  const [prizes, setPrizes] = useState("");
  const [rules, setRules] = useState("");

  const load = useCallback(async (m) => {
    try {
      const [c, u] = await Promise.all([
        api.get("/championship", { params: m ? { month: m } : {} }), api.get("/admin/championship/users")]);
      setCup(c.data); setMonth(c.data.month); setPrizes(c.data.prizes); setRules(c.data.rules || DEFAULT_RULES);
      setEnabled(u.data.enabled);
    } catch (e) { logError("cup admin load", e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveConfig = async () => {
    try { await api.put("/admin/championship/config", { month, prizes, rules }); toast.success("Premi e regolamento salvati"); }
    catch (err) { fail(err); }
  };

  const entries = Object.fromEntries((cup?.entries || []).map((e) => [e.user_id, e]));
  const rows = [...enabled, ...(cup?.entries || []).filter((e) => !enabled.some((u) => u.user_id === e.user_id))];

  return (
    <div data-testid="admin-championship" className="mb-8 bg-[#141414] border border-amber-500/25 rounded-md p-5">
      <div className="flex items-center gap-2 mb-1">
        <Trophy size={16} className="text-amber-400" />
        <h2 className="text-base font-semibold text-white">Championship · Trading Cup mensile</h2>
      </div>
      <p className="text-xs text-zinc-500 mb-4">Abilita i partecipanti, inserisci rendimento % e profitto $ del mese: la classifica pubblica si ordina per rendimento %.</p>

      <UserSearch onChanged={() => load(month)} />

      <div className="flex flex-wrap items-center gap-3 mt-6 mb-3">
        <label className="text-xs text-zinc-500 uppercase tracking-wider">Mese</label>
        <input data-testid="cup-admin-month" type="month" value={month} onChange={(e) => e.target.value && load(e.target.value)}
          className={`${inputCls} !w-48`} />
        <span className="text-xs text-zinc-500">{monthLabel(month)} · {enabled.length} abilitati</span>
      </div>
      <div className="space-y-2">
        {rows.length === 0 && <div className="text-sm text-zinc-500">Nessun partecipante abilitato.</div>}
        {rows.map((u) => (
          <EntryRow key={u.user_id} u={u} entry={entries[u.user_id]} month={month} onSaved={() => load(month)} />
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
        <div>
          <label className="block text-xs text-zinc-500 uppercase tracking-wider mb-1.5">Premi di {monthLabel(month)}</label>
          <textarea data-testid="cup-prizes-input" rows={4} value={prizes} onChange={(e) => setPrizes(e.target.value)}
            placeholder={"1° posto: ...\n2° posto: ..."} className={inputCls} />
        </div>
        <div>
          <label className="block text-xs text-zinc-500 uppercase tracking-wider mb-1.5">Regolamento (valido per tutti i mesi)</label>
          <textarea data-testid="cup-rules-input" rows={14} value={rules} onChange={(e) => setRules(e.target.value)} className={inputCls} />
        </div>
      </div>
      <button data-testid="cup-config-save-btn" onClick={saveConfig}
        className="mt-3 flex items-center gap-2 px-5 py-2 rounded-md bg-amber-600 text-white text-sm font-medium hover:bg-amber-500 transition-colors">
        <Save size={15} /> Salva premi e regolamento
      </button>
    </div>
  );
}
